import React, { useState, useRef, useCallback } from "react"
import { cn } from "@/lib/utils"
import { VideoPlayer } from "@/components/academician/video-player"
import { ChapterList, Chapter } from "@/components/academician/chapter-list"
import { LearningTabs, Resource, QuizQuestion } from "@/components/academician/learning-tabs"
import { Button } from "@/components/ui/button"
import { MenuIcon, XIcon, PanelLeftIcon, PanelRightIcon } from "lucide-react"
import { apiPost, apiDelete } from "@/lib/api"

interface SavedNote {
  id: number
  text: string
  timestamp: number
  formattedTimestamp: string
}

interface NextContent {
  type: string
  path: string
}

export interface VideoLearningPageProps {
  moduleTitle?: string
  videoTitle?: string
  videoUrl?: string
  progressSeconds?: number
  progressPercentage?: number
  isCompleted?: boolean
  videoPlatform?: string
  markCompleteUrl?: string
  updateProgressUrl?: string
  trackEventUrl?: string
  nextContent?: NextContent | null
  videoId?: number
  createNoteUrl?: string
  deleteNoteUrl?: string
  savedNotes?: SavedNote[]
  chapters?: Chapter[]
  quiz?: QuizQuestion[]
  resources?: Resource[]
  className?: string
}

export const VideoLearningPage: React.FC<VideoLearningPageProps> = ({
  moduleTitle = "React Fundamentals",
  videoTitle = "Understanding JSX and Components",
  videoUrl,
  progressSeconds = 0,
  progressPercentage = 0,
  isCompleted: initialCompleted = false,
  videoPlatform,
  markCompleteUrl,
  updateProgressUrl,
  trackEventUrl,
  nextContent: initialNextContent = null,
  videoId,
  createNoteUrl,
  deleteNoteUrl,
  savedNotes = [],
  chapters = [],
  quiz,
  resources = [],
  className
}) => {
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false)
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false)
  const [watchPercentage, setWatchPercentage] = useState(progressPercentage)
  const [isCompleted, setIsCompleted] = useState(initialCompleted)
  const [notes, setNotes] = useState(() =>
    savedNotes.map(n => `[${n.formattedTimestamp}] ${n.text}`).join("\n")
  )
  const [nextContent, setNextContent] = useState<NextContent | null>(initialNextContent)
  const [autoSaveStatus, setAutoSaveStatus] = useState<"saved" | "saving" | "unsaved">("saved")
  const [currentTimestamp, setCurrentTimestamp] = useState("00:00")
  const [noteIds, setNoteIds] = useState<number[]>(savedNotes.map(n => n.id))
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const formatTimestamp = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }

  const handleTimeUpdate = (seconds: number) => {
    setCurrentTimestamp(formatTimestamp(seconds))
  }

  const handleChapterClick = (chapterId: string) => {
    console.log("Navigate to chapter:", chapterId)
    setLeftSidebarOpen(false)
  }

  const handleMarkComplete = async () => {
    if (!markCompleteUrl) return

    try {
      const response = await apiPost(markCompleteUrl, {})
      const data = await response.json()
      if (data.success) {
        setIsCompleted(true)
        setWatchPercentage(100)
        if (data.next_content) {
          setNextContent(data.next_content)
        }
      }
    } catch (error) {
      console.error("Failed to mark complete:", error)
    }
  }

  const saveNotes = useCallback(async (text: string) => {
    if (!createNoteUrl || !videoId) return

    setAutoSaveStatus("saving")

    try {
      // Delete existing notes for this video
      for (const id of noteIds) {
        try {
          await apiDelete(`${deleteNoteUrl}/${id}`)
        } catch { /* note may already be deleted */ }
      }

      // Save each non-empty line as a separate note
      const lines = text.split("\n").filter(l => l.trim().length > 0)
      const newIds: number[] = []

      for (const line of lines) {
        // Parse timestamp if present: [MM:SS] text
        const match = line.match(/^\[(\d+):(\d+)\]\s*(.+)/)
        let timestamp = 0
        let noteText = line.trim()

        if (match) {
          timestamp = parseInt(match[1]) * 60 + parseInt(match[2])
          noteText = match[3].trim()
        }

        if (noteText.length === 0) continue

        try {
          const response = await apiPost(createNoteUrl, {
            video_note: {
              video_id: videoId,
              note_text: noteText,
              timestamp_seconds: timestamp
            }
          })
          const data = await response.json()
          if (data.success && data.note?.id) {
            newIds.push(data.note.id)
          }
        } catch {
          setAutoSaveStatus("unsaved")
          return
        }
      }

      setNoteIds(newIds)
      setAutoSaveStatus("saved")
    } catch {
      setAutoSaveStatus("unsaved")
    }
  }, [createNoteUrl, deleteNoteUrl, videoId, noteIds])

  const handleNotesChange = (newNotes: string) => {
    setNotes(newNotes)
    setAutoSaveStatus("unsaved")

    // Debounce save — wait 2 seconds after user stops typing
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
    }
    saveTimerRef.current = setTimeout(() => {
      saveNotes(newNotes)
    }, 2000)
  }

  return (
    <div className={cn("flex flex-col h-screen bg-background", className)}>
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-border bg-card">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
        >
          <MenuIcon className="w-5 h-5" />
        </Button>
        <h1 className="text-sm font-semibold text-foreground truncate">
          {moduleTitle}
        </h1>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
        >
          <MenuIcon className="w-5 h-5" />
        </Button>
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Chapter List */}
        <aside
          className={cn(
            "w-[250px] border-r border-border bg-card overflow-y-auto",
            "lg:block",
            "fixed lg:relative inset-y-0 left-0 z-40 lg:z-0",
            "transition-transform duration-300",
            leftSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          )}
        >
          {/* Mobile Close Button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLeftSidebarOpen(false)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>

          {/* Desktop Toggle */}
          <div className="hidden lg:flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Chapters</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLeftSidebarOpen(!leftSidebarOpen)}
              className="lg:hidden"
            >
              <PanelLeftIcon className="w-4 h-4" />
            </Button>
          </div>

          <ChapterList
            moduleTitle={moduleTitle}
            chapters={chapters}
            onChapterClick={handleChapterClick}
          />
        </aside>

        {/* Center - Video Player */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto p-4 md:p-6 lg:p-8">
            <VideoPlayer
              title={videoTitle}
              videoUrl={videoUrl}
              watchPercentage={watchPercentage}
              isCompleted={isCompleted}
              onMarkComplete={handleMarkComplete}
              onProgressChange={setWatchPercentage}
              onTimeUpdate={handleTimeUpdate}
              videoPlatform={videoPlatform}
              initialSeconds={progressSeconds}
              updateProgressUrl={updateProgressUrl}
              trackEventUrl={trackEventUrl}
              nextContentUrl={nextContent?.path}
              nextContentType={nextContent?.type}
            />
          </div>
        </main>

        {/* Right Sidebar - Learning Tabs */}
        <aside
          className={cn(
            "w-[300px] border-l border-border bg-card overflow-y-auto",
            "lg:block",
            "fixed lg:relative inset-y-0 right-0 z-40 lg:z-0",
            "transition-transform duration-300",
            rightSidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
          )}
        >
          {/* Mobile Close Button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Learning Tools</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRightSidebarOpen(false)}
            >
              <XIcon className="w-4 h-4" />
            </Button>
          </div>

          {/* Desktop Toggle */}
          <div className="hidden lg:flex items-center justify-between p-4 border-b border-border">
            <h2 className="text-sm font-semibold text-foreground">Learning Tools</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
              className="lg:hidden"
            >
              <PanelRightIcon className="w-4 h-4" />
            </Button>
          </div>

          <div className="p-4">
            <LearningTabs
              notes={notes}
              onNotesChange={handleNotesChange}
              currentTimestamp={currentTimestamp}
              quiz={quiz}
              resources={resources}
              autoSaveStatus={autoSaveStatus}
            />
          </div>
        </aside>
      </div>

      {/* Mobile Overlay */}
      {(leftSidebarOpen || rightSidebarOpen) && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => {
            setLeftSidebarOpen(false)
            setRightSidebarOpen(false)
          }}
        />
      )}
    </div>
  )
}
